// dependencies
import React, { useState } from "react";

import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { faFloppyDisk } from "@fortawesome/pro-solid-svg-icons";

import { toast } from "@performyard/py-components";

import Button from "../../../../../../../components/Button";
import TextInput from "../../../../../../../components/Inputs/InputText";
// components
import Spacer from "../../../../../../../components/Spacer";

import useDebouncedValue from "../../../../../../../hooks/useDebouncedValue";
import useNavigate from "../../../../../../../hooks/useNavigate";

// api
import { createStaticCohort, fetchSelectionObjectCount } from "../../../../../../../api/services";

// utils
import get from "../../../../../../../utils/GetNested";

import Directory from "../../../../Directory";
// schema
import { FormStateProps } from "../../index";
// styles
import css from "./Cohorts.module.scss";

interface CustomError extends Error {
	data: {
		errors: string[];
	};
}

const columns = [
	{
		id: "Group",
		label: "PY Group",
		isSortable: true,
		getter: (row) => {
			return get(row, ["data_fields", "Group", "value", "name"], "");
		},
	},
	{
		id: "Manager",
		label: "Manager",
		isSortable: true,
		getter: (row) => {
			return get(row, ["data_fields", "Manager", "value", "name"], "");
		},
	},
];

const StaticCohortEmployees = (props: Pick<FormStateProps, "state">) => {
	const { state } = props;

	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const debouncedSearch = useDebouncedValue(search, 300);
	const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
	const [isSaving, setIsSaving] = useState(false);

	const handleCreateCohort = async () => {
		try {
			if (state.selectionObjectID) {
				await createStaticCohort(state.name, state.description, state.color, state.selectionObjectID);

				navigate("/administration/engagement/cohorts");

				try {
					const userSelectionResponse = await fetchSelectionObjectCount(state.selectionObjectID);
					toast(`${state.name} created with ${userSelectionResponse.count} employees`, {
						type: "success",
						size: "large",
					});
				} catch (error) {
					console.error(error);
				}
			}
		} catch (error) {
			const errorMessage = (error as CustomError)?.data?.errors[0] || "Failed to save new dynamic cohort";

			toast(errorMessage, {
				type: "error",
				size: "large",
			});
		}
	};

	if (!state.selectionObjectID) {
		return <div>Loading...</div>;
	}

	return (
		<>
			<div className={css.directoryWrapper}>
				<div className={css.controls}>
					<div className={css.search}>
						<TextInput
							icon={faSearch}
							value={search}
							onChange={setSearch}
							placeholder="Search Employees..."
							clearable
						/>
					</div>
					<div />
					<div />
					<div>
						<Button
							onClick={async () => {
								setIsSaving(true);
								try {
									await handleCreateCohort();
								} catch (error) {
									toast.error((error as Error).message);
								} finally {
									setIsSaving(false);
								}
							}}
							color="green"
							icon={faFloppyDisk}
							iconPosition="left"
							disabled={selectedEmployees.length <= 0}
							isRequesting={isSaving}
							className={css.saveButton}
						>
							Save & Complete
						</Button>
					</div>
				</div>

				<Spacer height={2} />

				<Directory
					columns={columns}
					stagingUserSelectionID={state.selectionObjectID}
					selectedEmployees={selectedEmployees}
					setSelectedEmployees={setSelectedEmployees}
					employeeSearchProps={{
						searchTerm: debouncedSearch,
						limitSelected: false,
						userSelectionID: state.selectionObjectID,
					}}
					canBulkSelect
					emptyText={
						debouncedSearch
							? "A search on the employee list results in no matches."
							: "No employees to display."
					}
				/>
			</div>
		</>
	);
};

export default StaticCohortEmployees;
